import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { Skeleton } from "../components/ui/Skeleton";
import {
  complianceApi,
  ComplianceDashboard,
  ClientComplianceRow,
  ComplianceStatus,
  GeneratedComplianceDocument,
  CancellationStatus,
  ComplianceChecklistSummaryItem,
} from "../api/complianceApi";

function StatusOrb({ status }: { status: string }) {
  const s = status.toLowerCase();
  const cls =
    s === "red" || s === "non-compliant"
      ? "bg-rose-500 shadow-rose-500/50"
      : s === "yellow" || s === "at risk"
        ? "bg-amber-400 shadow-amber-400/40"
        : "bg-emerald-500 shadow-emerald-500/40";
  return (
    <span
      className={`inline-block h-3 w-3 rounded-full shadow-[0_0_8px] ${cls}`}
      title={status}
      aria-label={`Compliance ${status}`}
    />
  );
}

function ChecklistRow({ item }: { item: ComplianceStatus["checklist"][0] }) {
  const s = item.status.toLowerCase();
  const border =
    s === "fail"
      ? "border-rose-500/40 bg-rose-950/25"
      : s === "warning"
        ? "border-amber-500/35 bg-amber-950/20"
        : "border-emerald-500/30 bg-emerald-950/15";
  const label =
    s === "fail" ? "text-rose-200" : s === "warning" ? "text-amber-200" : "text-emerald-200";
  return (
    <div className={`rounded-lg border px-3 py-2 text-sm ${border}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-slate-100">{item.label}</span>
        <span className={`text-xs font-semibold uppercase tracking-wide ${label}`}>{item.status}</span>
      </div>
      {item.detail && <p className="mt-1 text-xs text-slate-400">{item.detail}</p>}
    </div>
  );
}

function SummaryCheckCard({ item }: { item: ComplianceChecklistSummaryItem }) {
  const s = item.status.toLowerCase();
  const border =
    s === "fail"
      ? "border-rose-500/40 bg-rose-950/20"
      : s === "warning"
        ? "border-amber-500/35 bg-amber-950/15"
        : "border-emerald-500/35 bg-emerald-950/15";
  const badge =
    s === "fail" ? "bg-rose-500/20 text-rose-200" : s === "warning" ? "bg-amber-500/20 text-amber-200" : "bg-emerald-500/20 text-emerald-200";
  return (
    <div className={`rounded-xl border p-4 ${border}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{item.code.replace(/_/g, " ")}</p>
      <h3 className="mt-1 text-sm font-semibold text-white">{item.label}</h3>
      <span className={`mt-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${badge}`}>{item.status}</span>
      <p className="mt-2 text-xs leading-relaxed text-slate-400">{item.detail}</p>
    </div>
  );
}

function ComplianceCell({ ok, okLabel, missingLabel }: { ok: boolean; okLabel: string; missingLabel: string }) {
  return ok ? (
    <span className="text-emerald-400" title={okLabel} aria-label={okLabel}>
      ✓ OK
    </span>
  ) : (
    <span className="text-amber-300" title={missingLabel} aria-label={missingLabel}>
      ⚠ Missing
    </span>
  );
}

function statusBadgeClass(status: string): string {
  const s = status.toLowerCase();
  if (s === "compliant") {
    return "border-emerald-500/40 bg-emerald-500/10 text-emerald-200";
  }
  if (s === "at risk") {
    return "border-amber-500/40 bg-amber-500/10 text-amber-200";
  }
  return "border-rose-500/40 bg-rose-500/10 text-rose-200";
}

const CompliancePage: React.FC = () => {
  const [dashboard, setDashboard] = useState<ComplianceDashboard | null>(null);
  const [clients, setClients] = useState<ClientComplianceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [detailByClient, setDetailByClient] = useState<Record<string, ComplianceStatus>>({});
  const [cancelByClient, setCancelByClient] = useState<Record<string, CancellationStatus>>({});
  const [detailLoading, setDetailLoading] = useState<string | null>(null);
  const [docModal, setDocModal] = useState<GeneratedComplianceDocument | null>(null);
  const [docLoading, setDocLoading] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, c] = await Promise.all([complianceApi.getDashboard(), complianceApi.getClientComplianceList()]);
      setDashboard(d);
      setClients(c);
    } catch {
      setError("Could not load compliance data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const needsAttentionCount = useMemo(
    () => clients.filter((c) => c.status !== "Compliant").length,
    [clients]
  );

  const firstClientForUpload = useMemo(
    () => clients.find((c) => !c.hasContract || !c.hasDisclosure),
    [clients]
  );

  const scoreAccent = useMemo(() => {
    const s = dashboard?.complianceStatus?.toLowerCase() ?? "";
    if (s === "compliant") {
      return "from-emerald-500/30 to-emerald-600/10 border-emerald-500/40 text-emerald-200";
    }
    if (s === "at risk") {
      return "from-amber-500/25 to-amber-600/10 border-amber-500/40 text-amber-200";
    }
    return "from-rose-500/25 to-rose-600/10 border-rose-500/40 text-rose-200";
  }, [dashboard?.complianceStatus]);

  const toggleExpand = async (clientId: string) => {
    if (expanded === clientId) {
      setExpanded(null);
      return;
    }
    setExpanded(clientId);
    if (detailByClient[clientId] && cancelByClient[clientId]) {
      return;
    }
    setDetailLoading(clientId);
    try {
      const [status, cancel] = await Promise.all([
        complianceApi.getStatus(clientId),
        complianceApi.getCancellationStatus(clientId),
      ]);
      setDetailByClient((m) => ({ ...m, [clientId]: status }));
      setCancelByClient((m) => ({ ...m, [clientId]: cancel }));
    } catch {
      setError("Could not load client compliance detail.");
    } finally {
      setDetailLoading(null);
    }
  };

  const generateDoc = async (clientId: string, kind: "disclosure") => {
    setDocLoading(`${clientId}-${kind}`);
    setError(null);
    try {
      const doc = await complianceApi.generateDisclosure(clientId);
      setDocModal(doc);
    } catch {
      setError("Failed to generate CROA disclosure.");
    } finally {
      setDocLoading(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-semibold text-white">Compliance dashboard</h1>
          <p className="mt-1 text-sm text-slate-400">
            CROA-focused monitoring: contracts, disclosures, fee timing, and AI guardrails. This view informs your team;
            it does not block actions.
          </p>
        </div>

        {needsAttentionCount > 0 && (
          <div className="rounded-xl border border-rose-500/50 bg-rose-950/40 px-4 py-3 text-sm text-rose-100 shadow-[0_0_24px_-8px_rgba(244,63,94,0.5)]">
            <p className="font-semibold text-rose-200">
              {needsAttentionCount} client{needsAttentionCount === 1 ? "" : "s"} need attention
            </p>
            <p className="mt-1 text-rose-100/90">
              Review the table and alerts below before charging fees or expanding services.
            </p>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-rose-500/40 bg-rose-950/30 px-4 py-2 text-sm text-rose-200">
            {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-4" aria-busy="true" aria-label="Loading compliance dashboard">
            <Skeleton className="h-40 w-full rounded-xl" />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-32 rounded-xl" />
              ))}
            </div>
            <Skeleton className="h-64 w-full rounded-xl" />
          </div>
        ) : (
          <>
            {dashboard && (
              <div
                className={`rounded-2xl border bg-gradient-to-br p-6 ${scoreAccent}`}
              >
                <div className="flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Compliance score</p>
                    <p className="mt-1 text-5xl font-bold tabular-nums text-white">{dashboard.complianceScore}</p>
                    <p className="mt-2 text-lg font-medium text-slate-100">{dashboard.complianceStatus}</p>
                    <p className="mt-1 max-w-xl text-sm text-slate-400">
                      Weighted view: contracts (40%), disclosures (30%), no upfront-fee flags (20%), AI guardrails (10%).
                      {dashboard.totalClients === 0 ? " Add clients to see a tenant-relative score." : ""}
                    </p>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-slate-950/40 px-4 py-3 text-sm text-slate-300">
                    <p>
                      <span className="text-slate-500">Clients</span>{" "}
                      <span className="font-semibold text-white">{dashboard.totalClients}</span>
                    </p>
                    <p className="mt-1">
                      <span className="text-slate-500">Upfront-fee flags</span>{" "}
                      <span
                        className={
                          dashboard.upfrontBillingViolations > 0 ? "font-semibold text-rose-300" : "font-semibold text-emerald-300"
                        }
                      >
                        {dashboard.upfrontBillingViolations}
                      </span>
                    </p>
                    <p className="mt-1 flex items-center gap-2">
                      <span className="text-slate-500">AI guardrails</span>
                      <span className="font-semibold text-emerald-300">
                        {dashboard.aiGuardrailsActive ? "Active" : "—"}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            <section id="croa-checklist" className="space-y-3">
              <h2 className="text-lg font-semibold text-white">CROA checklist</h2>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {dashboard?.checklist.map((item) => (
                  <SummaryCheckCard key={item.code} item={item} />
                ))}
              </div>
            </section>

            {dashboard && dashboard.alerts.length > 0 && (
              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-white">Alerts</h2>
                <ul className="space-y-2">
                  {dashboard.alerts.map((a, i) => {
                    const sev = a.severity.toLowerCase();
                    const box =
                      sev === "critical"
                        ? "border-rose-500/50 bg-rose-950/35 text-rose-100"
                        : sev === "warning"
                          ? "border-amber-500/45 bg-amber-950/25 text-amber-100"
                          : "border-slate-600 bg-slate-900/80 text-slate-200";
                    return (
                      <li key={i} className={`rounded-lg border px-4 py-3 text-sm ${box}`}>
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{a.severity}</span>
                        <p className="mt-1">{a.message}</p>
                        {a.clientId && (
                          <Link
                            to={`/clients/${a.clientId}`}
                            className="mt-2 inline-block text-xs font-medium text-blue-400 hover:text-blue-300"
                          >
                            Open client →
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
              <h2 className="text-sm font-semibold text-white">Quick actions</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to={firstClientForUpload ? `/clients/${firstClientForUpload.clientId}` : "/clients"}
                  className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200 hover:bg-slate-700"
                >
                  Upload a contract (client Documents tab)
                </Link>
                <button
                  type="button"
                  disabled={!firstClientForUpload || docLoading !== null}
                  onClick={() => firstClientForUpload && void generateDoc(firstClientForUpload.clientId, "disclosure")}
                  className="rounded-lg border border-rose-500/40 bg-rose-950/40 px-3 py-2 text-sm text-rose-100 hover:bg-rose-950/60 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Generate disclosure text
                </button>
                <a
                  href="#croa-checklist"
                  className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200 hover:bg-slate-700"
                >
                  View CROA checklist
                </a>
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-lg font-semibold text-white">Client compliance</h2>
              <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/90">
                {clients.length === 0 ? (
                  <p className="p-8 text-center text-slate-400">No clients yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm text-slate-200">
                      <thead className="border-b border-slate-800 bg-slate-950/50 text-xs uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-4 py-3">Client</th>
                          <th className="px-4 py-3">Contract</th>
                          <th className="px-4 py-3">Disclosure</th>
                          <th className="px-4 py-3">No upfront fee</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                        {clients.map((row) => {
                          const open = expanded === row.clientId;
                          const detail = detailByClient[row.clientId];
                          const cancel = cancelByClient[row.clientId];
                          return (
                            <React.Fragment key={row.clientId}>
                              <tr className="bg-slate-950/20">
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <StatusOrb status={row.status} />
                                    <Link
                                      to={`/clients/${row.clientId}`}
                                      className="font-medium text-white hover:text-blue-300"
                                    >
                                      {row.clientName}
                                    </Link>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <ComplianceCell
                                      ok={row.hasContract}
                                      okLabel="Contract or signed agreement on file"
                                      missingLabel="Missing contract or agreement"
                                    />
                                    {row.hasPendingContract && !row.hasContract && (
                                      <span
                                        className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-200"
                                        title="A contract is sent but not yet signed"
                                      >
                                        Awaiting signature
                                      </span>
                                    )}
                                    {row.hasPendingContract && row.hasContract && (
                                      <span
                                        className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-200"
                                        title="A new contract is in progress"
                                      >
                                        New contract pending
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <ComplianceCell
                                    ok={row.hasDisclosure}
                                    okLabel="Disclosure documented"
                                    missingLabel="Missing disclosure"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <ComplianceCell
                                    ok={row.noUpfrontBilling}
                                    okLabel="No upfront-fee flag"
                                    missingLabel="Possible upfront fee concern"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <span
                                    className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${statusBadgeClass(row.status)}`}
                                  >
                                    {row.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <div className="flex flex-wrap justify-end gap-2">
                                    <button
                                      type="button"
                                      onClick={() => void toggleExpand(row.clientId)}
                                      className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-700"
                                    >
                                      {open ? "Hide" : "Detail"}
                                    </button>
                                    <button
                                      type="button"
                                      disabled={docLoading === `${row.clientId}-disclosure`}
                                      onClick={() => void generateDoc(row.clientId, "disclosure")}
                                      className="rounded-lg border border-rose-500/40 bg-rose-950/40 px-3 py-1.5 text-xs font-medium text-rose-100 hover:bg-rose-950/60 disabled:opacity-50"
                                    >
                                      {docLoading === `${row.clientId}-disclosure` ? "…" : "CROA text"}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              {open && (
                                <tr className="bg-slate-950/50">
                                  <td colSpan={6} className="px-4 py-4">
                                    {detailLoading === row.clientId && !detail && (
                                      <p className="text-sm text-slate-500">Loading checklist…</p>
                                    )}
                                    {detail && (
                                      <div className="space-y-2">
                                        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                          Detailed CROA checklist
                                        </h4>
                                        {detail.checklist.map((item) => (
                                          <ChecklistRow key={item.code} item={item} />
                                        ))}
                                      </div>
                                    )}
                                    {cancel && (
                                      <div
                                        className={`mt-4 rounded-lg border px-3 py-3 text-sm ${
                                          cancel.threeDayWindowHasPassed
                                            ? "border-emerald-500/30 bg-emerald-950/20 text-emerald-100"
                                            : "border-amber-500/35 bg-amber-950/25 text-amber-100"
                                        }`}
                                      >
                                        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                                          3-day cancellation tracker
                                        </h4>
                                        <p className="mt-2 text-slate-200">{cancel.message}</p>
                                        {cancel.windowEndsAt && (
                                          <p className="mt-1 text-xs text-slate-400">
                                            Window ends (date): {cancel.windowEndsAt.slice(0, 10)}
                                            {cancel.wholeDaysRemaining != null && cancel.wholeDaysRemaining > 0
                                              ? ` · ~${cancel.wholeDaysRemaining} day(s) left`
                                              : null}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </div>

      {docModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex max-h-[90vh] w-full max-w-3xl min-w-0 flex-col rounded-xl border border-slate-700 bg-slate-900 shadow-2xl">
            <div className="border-b border-slate-800 px-4 py-3">
              <h2 className="text-lg font-semibold text-white">{docModal.title}</h2>
              <p className="text-xs text-slate-500">{new Date(docModal.generatedAt).toLocaleString()}</p>
            </div>
            <pre className="flex-1 overflow-y-auto whitespace-pre-wrap p-4 text-xs text-slate-300">
              {docModal.content}
            </pre>
            <div className="flex flex-wrap justify-end gap-2 border-t border-slate-800 px-4 py-3">
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(docModal.content);
                }}
                className="rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800"
              >
                Copy text
              </button>
              <button
                type="button"
                onClick={() => setDocModal(null)}
                className="rounded-lg bg-slate-700 px-4 py-2 text-sm text-white hover:bg-slate-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default CompliancePage;
